'use client';
import { useEffect, useRef, useState } from 'react';
import { X, Info } from 'lucide-react';
import { createClient } from '@/utils/supabase/client';
import usePollingQuery from '@/utils/hooks/usePollingQuery';
import { TrackingEvent } from '@/utils/helpers';
import { analytics } from '@/lib/analytics';

const LogInner = ({ id }: { id?: number }) => {
  const supabase = createClient();
  const { data: trainings } = usePollingQuery<'trainings'>({
    supabase,
    table: 'trainings',
    select: 'logs, id, is_success, is_error',
    filter: ['id', 'eq', id],
    intervalMs: 2000,
    stopWhen: (rows) => rows.length > 0 && rows.every((r) => r.is_success || r.is_error),
  });

  const training = trainings?.[0];
  const logs = training?.logs || '';

  return <pre className="whitespace-pre-wrap break-words font-mono text-sm">{logs}</pre>;
};

export const LogModal = ({ id, tracking }: { id?: number; tracking?: TrackingEvent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (isOpen && tracking) {
      analytics.capture(tracking.event, tracking.properties);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <div className="font-sans">
      <button
        onClick={toggleModal}
        className={
          'text-white flex gap-2 items-center font-mono border border-white rounded-lg px-3 py-1'
        }
      >
        <Info size={16} />
        Logs
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <div
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-3xl h-3/4 rounded-xl shadow-xl flex flex-col"
          >
            <div className="flex justify-between items-center p-4 border-zinc-700">
              <h2 className="text-xl font-semibold">Logs</h2>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="flex-grow overflow-auto p-4">
              <LogInner id={id} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
